import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { HEADLINE_SUFFIX, HEADLINE_WORDS } from './authHeadlineWords';

const INTERVAL_MS = 2200;
const OUT_MS = 350;
const IN_MS = 380;

/**
 * 순환 헤드라인 — 글로릴스 로그인 화면의 시그니처 모션을 소듀 문구로 재현.
 *
 * transform/opacity 만 움직여 하드웨어 가속 범위를 벗어나지 않는다.
 * prefers-reduced-motion 이면 모션 없이 즉시 교체한다.
 */
export default function AuthHeadline({ compact = false }: { compact?: boolean }) {
    const [word, setWord] = useState<string>(HEADLINE_WORDS[0]);
    const [phase, setPhase] = useState<'idle' | 'out' | 'in'>('idle');
    const idx = useRef(0);

    useEffect(() => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let t1: ReturnType<typeof setTimeout>;
        let t2: ReturnType<typeof setTimeout>;

        const id = setInterval(() => {
            idx.current = (idx.current + 1) % HEADLINE_WORDS.length;
            const next = HEADLINE_WORDS[idx.current];

            if (reduce) {
                setWord(next);
                return;
            }

            setPhase('out');
            t1 = setTimeout(() => {
                setWord(next);
                setPhase('in');
                t2 = setTimeout(() => setPhase('idle'), IN_MS);
            }, OUT_MS);
        }, INTERVAL_MS);

        return () => {
            clearInterval(id);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    return (
        <Box>
            <Typography
                variant={compact ? 'h5' : 'h3'}
                component="h1"
                sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.25,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        minHeight: '1.3em',
                        color: 'primary.main',
                        willChange: 'transform, opacity',
                        animation:
                            phase === 'out'
                                ? `authWordOut ${OUT_MS}ms ease-in forwards`
                                : phase === 'in'
                                  ? `authWordIn ${IN_MS}ms ease-out`
                                  : 'none',
                        '@keyframes authWordOut': {
                            from: { opacity: 1, transform: 'translateY(0)' },
                            to: { opacity: 0, transform: 'translateY(-0.35em)' },
                        },
                        '@keyframes authWordIn': {
                            from: { opacity: 0, transform: 'translateY(0.35em)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                    }}
                >
                    {word}
                </Box>
                <br />
                {HEADLINE_SUFFIX}
            </Typography>
        </Box>
    );
}
